import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/global-views/global-views.png",
        title: "Global Views MENA's growth story: From manual chaos to seamless messaging",
        sector: "Retail & Distribution",
        externalLink: "/case-study/global-views",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/til-limited/til-limited.png",
        title: "TIL Limited boosts lead generation and customer engagement with Gallabox",
        sector: "Manufacturing",
        externalLink: "/case-study/til-limited",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/inlingua/inlingua.png",
        title: "Inlingua doubles growth by streamlining student engagement with Gallabox",
        sector: "Education",
        externalLink: "/case-study/inlingua",
    },
];

const Konze: React.FC = () => {
    return (
        <NewLayout
            title="How KONZE streamlined lead generation with Gallabox | Case Study"
            description="Discover how KONZE transformed its outreach process with Gallabox's easy-to-use WhatsApp automation platform — improving productivity, leads, and conversions by 70%."
            canonical="https://gallabox.com/case-study/konze"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/konze/konze-og.png" }}
        >
            <CaseStudyHerosection
                title="Simplifying communication, amplifying growth: KONZE's success with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/konze/konze-hero.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "70%", label: "improvement in lead generation" },
                    { percentage: "70%", label: "increase in employee productivity" },
                    { percentage: "70%", label: "growth in first-time customer conversion rates" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Technology & Education Solutions"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [{ link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" }],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About KONZE">
                            <CaseStudyInformation subTitle={"About KONZE"} className="mt-0" />
                            <p className="mt-2">
                                KONZE is a technology-driven company that provides digital solutions to simplify business operations for education, immigration, and consulting
                                enterprises. With a strong focus on delivering efficiency and scalability, KONZE helps its clients leverage technology to streamline workflows and
                                enhance productivity.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <p className="mt-2">
                                As the business expanded, the team at KONZE began exploring new ways to reach prospective clients faster and engage them effectively through
                                conversational channels like WhatsApp — a platform that most of their audience preferred for business communication.
                            </p>
                            <p className="mt-5">
                                Unlike many organizations that struggle with fragmented systems or slow response times, KONZE&apos;s challenge wasn&apos;t rooted in operational
                                inefficiencies. The company already had a structured communication setup in place.
                            </p>
                            <p className="mt-5">
                                However, the team recognized an opportunity to optimize its lead generation process and ensure a more personalized, scalable, and instant way to
                                connect with potential customers. KONZE wanted to move beyond traditional outreach methods and tap into WhatsApp&apos;s growing potential for
                                business communication.
                            </p>
                            <p className="mt-5">This shift wasn&apos;t driven by fixing problems but by adopting smarter tools to scale faster.</p>
                            <CaseStudyInformation subTitle={"Initial solutions tried"} className="mt-10" />
                            <p className="mt-2">
                                Before discovering Gallabox, KONZE experimented with WATI, a well-known WhatsApp API provider. While the tool performed basic messaging functions,
                                the team found it restrictive in terms of usability and flexibility.
                            </p>
                            <p className="mt-5">Some of the limitations included:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Complex interface:",
                                        nestedListItem: ["Team members needed more time to adapt, slowing execution."],
                                    },
                                    {
                                        listItem: "Limited scalability:",
                                        nestedListItem: ["Managing larger campaigns or real-time engagement required additional steps."],
                                    },
                                    {
                                        listItem: "Inconsistent support:",
                                        nestedListItem: ["Customization and troubleshooting often took longer than expected."],
                                    },
                                ]}
                                className="mt-2 bg-gray-100 "
                            />
                            <p className="mt-5">These gaps prompted KONZE to explore an alternative that could deliver simplicity without sacrificing capability.</p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why KONZE chose Gallabox"} className="mt-0" />
                            <p className="mt-2">
                                Gallabox immediately stood out for its ease of use. The platform&apos;s intuitive interface meant that the KONZE team could launch campaigns, send
                                broadcasts, and monitor performance — all without technical barriers or long onboarding cycles.
                            </p>
                            <p className="mt-5">The deciding factors included:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "User-friendly experience:",
                                        nestedListItem: ["Easy to navigate, enabling faster setup and adoption."],
                                    },
                                    {
                                        listItem: "Streamlined communication:",
                                        nestedListItem: ["Allowed KONZE to connect with a wider audience using personalized broadcasts."],
                                    },
                                    {
                                        listItem: "Responsive support team:",
                                        nestedListItem: ["Quick assistance and guidance during setup and scaling ensured smooth implementation."],
                                    },
                                ]}
                                className="mt-5 bg-gray-100 "
                            />
                            <p className="mt-5">
                                By choosing Gallabox, KONZE not only simplified its outreach process but also unlocked the ability to generate leads more effectively and
                                consistently.
                            </p>
                            <CaseStudyInformation subTitle={"Goals with Gallabox"} className="mt-10" />
                            <p className="mt-2">KONZE implemented Gallabox with a clear focus on improving its lead generation workflow. The goals were to:</p>
                            <FeatureList
                                List={[
                                    { listItem: "Generate new leads efficiently through automated WhatsApp campaigns." },
                                    { listItem: "Maintain consistent communication with prospects and clients." },
                                    { listItem: "Increase productivity by reducing time spent on manual follow-ups." },
                                ]}
                                className="mt-4 bg-gray-100 "
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on business growth"} className="mt-0" />
                            <p className="mt-2">The adoption of Gallabox had a measurable impact on KONZE&apos;s business metrics:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "70% improvement in lead generation, achieved through consistent broadcast campaigns and instant customer engagement.",
                                    },
                                    {
                                        listItem: "70% increase in employee productivity, as the platform reduced manual workload and improved coordination.",
                                    },
                                    {
                                        listItem: "70% growth in first-time customer conversion rates, driven by faster and more personalized communication.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">
                                Overall, Gallabox played a significant role in helping KONZE scale communication and sales efficiency — empowering the team to manage outreach,
                                engagement, and conversion seamlessly within a single platform.
                            </p>

                            <CaseStudyInformation subTitle={"Where ease meets impact"} className="mt-10" />
                            <p className="mt-2">
                                KONZE&apos;s story is proof that technology-led organizations can still find room for transformation when they embrace the right tools. By using
                                Gallabox, KONZE has made its customer communication smarter, faster, and more scalable, achieving measurable improvements across productivity, lead
                                generation, and conversion.
                            </p>
                            <p className="mt-5">
                                For a company built on innovation, Gallabox became the perfect partner to extend that innovation to customer engagement, turning simple broadcasts
                                into meaningful business conversations that drive growth.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default Konze;
